/**
 * GameGuide Component
 *
 * Comprehensive game guide covering all mechanics.
 * Written from scratch with accurate game data.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HelpCircle, X, ChevronDown, ChevronUp,
  Swords, Target, Heart, Shield, Brain, Wind,
  Users, Gift, Trophy, Medal, Sparkles,
  Flame, Star, Zap
} from 'lucide-react';

// Collapsible section component
const GuideSection = ({ title, icon: Icon, iconColor, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-pocket-bg rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 bg-white hover:bg-pocket-bg/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon size={18} className={iconColor || 'text-pocket-red'} />}
          <span className="font-bold text-pocket-text">{title}</span>
        </div>
        {isOpen ? <ChevronUp size={18} className="text-pocket-text-light" /> : <ChevronDown size={18} className="text-pocket-text-light" />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-3 pt-0 space-y-3 text-sm">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Sub-section with title
const SubSection = ({ title, children }) => (
  <div>
    <h4 className="font-bold text-pocket-text mb-1">{title}</h4>
    <div className="text-pocket-text-light ml-2 space-y-1">{children}</div>
  </div>
);

const GameGuide = ({ isModal = false, onClose, showHeader = true }) => {
  const content = (
    <div className="space-y-3">
      {/* Game Overview */}
      <GuideSection title="Welcome to Pokesume" icon={Star} iconColor="text-amber-500" defaultOpen={true}>
        <div className="bg-pocket-bg rounded-lg p-3">
          <p className="text-pocket-text mb-2">
            Pokesume is a Pokemon training sim where you raise champions through 64 turns of training, battles, and events. Build your Hall of Fame to compete in tournaments and PvP!
          </p>
          <div className="space-y-1 text-pocket-text-light">
            <p>• <strong>Gacha:</strong> Unlock Pokemon and Support Cards with Primos</p>
            <p>• <strong>Career Mode:</strong> 64-turn journey to become Champion</p>
            <p>• <strong>Gyms & Elite Four:</strong> Major battles at turns 12, 24, 36, 48, 60-63</p>
            <p>• <strong>Inspirations:</strong> Hall of Fame Pokemon boost future careers</p>
            <p>• <strong>Compete:</strong> 12-hour tournaments and PvP matchmaking</p>
          </div>
        </div>
      </GuideSection>

      {/* Stats */}
      <GuideSection title="Pokemon Stats" icon={Target} iconColor="text-pocket-red">
        <SubSection title="The Five Stats">
          <div className="grid grid-cols-1 gap-1">
            <div className="flex items-start gap-2">
              <Heart size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
              <div><strong className="text-pocket-text">HP:</strong> Health pool. Battle ends when HP reaches 0.</div>
            </div>
            <div className="flex items-start gap-2">
              <Swords size={14} className="text-orange-500 mt-0.5 flex-shrink-0" />
              <div><strong className="text-pocket-text">Attack:</strong> Damage output (reduced by opponent's Defense).</div>
            </div>
            <div className="flex items-start gap-2">
              <Shield size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
              <div><strong className="text-pocket-text">Defense:</strong> Reduces incoming damage.</div>
            </div>
            <div className="flex items-start gap-2">
              <Brain size={14} className="text-purple-500 mt-0.5 flex-shrink-0" />
              <div><strong className="text-pocket-text">Instinct:</strong> Critical hit chance and dodge chance.</div>
            </div>
            <div className="flex items-start gap-2">
              <Wind size={14} className="text-teal-500 mt-0.5 flex-shrink-0" />
              <div><strong className="text-pocket-text">Speed:</strong> Stamina regeneration and cooldown reduction.</div>
            </div>
          </div>
        </SubSection>

        <SubSection title="Training Gains Per Success">
          <div className="bg-pocket-bg rounded p-2">
            <div className="grid grid-cols-5 gap-1 text-center text-xs mb-1">
              <div><span className="text-red-500 font-bold">HP</span><br/>+11</div>
              <div><span className="text-orange-500 font-bold">ATK</span><br/>+9</div>
              <div><span className="text-blue-500 font-bold">DEF</span><br/>+9</div>
              <div><span className="text-purple-500 font-bold">INS</span><br/>+7</div>
              <div><span className="text-teal-500 font-bold">SPD</span><br/>+5</div>
            </div>
            <p className="text-pocket-text-light text-xs">Plus: +3 Skill Points, +7 Friendship with support</p>
            <p className="text-pocket-text-light text-xs mt-1">Failure: -2 to trained stat, energy still consumed</p>
          </div>
        </SubSection>

        <SubSection title="Training Levels">
          <p className="text-xs">Each stat has its own training level (max 5). Every 4 successful trainings levels it up, giving <strong>+25% gains</strong> per level.</p>
          <div className="bg-pocket-bg rounded p-2 text-xs mt-1">
            <p>Level 1: Base gains | Level 5: +125% gains</p>
            <p className="text-pocket-text-light">Example: Level 5 Attack training = 9 × 2.25 = 20+ per success</p>
          </div>
        </SubSection>

        <SubSection title="Stat Preferences">
          <p className="text-xs">Set at career start based on Pokemon's strengths:</p>
          <div className="bg-pocket-bg rounded p-2 text-xs space-y-1">
            <p><strong>Preferred Stat:</strong> +20% training gains</p>
            <p><strong>Secondary Stat:</strong> +10% training gains</p>
          </div>
        </SubSection>

        <SubSection title="Battle Formulas">
          <div className="bg-pocket-bg rounded p-2 space-y-1 text-xs">
            <p><strong>Crit Chance:</strong> Instinct ÷ 900</p>
            <p><strong>Dodge Chance:</strong> Instinct ÷ 1800 (per tick)</p>
            <p><strong>Stamina Regen:</strong> Speed ÷ 15 (per tick)</p>
            <p><strong>Cooldown Reduction:</strong> -1 tick per 300 Speed</p>
            <p className="text-pocket-text-light mt-1">Crits deal 1.5× damage. Dodges negate damage completely.</p>
          </div>
        </SubSection>
      </GuideSection>

      {/* Career Mode */}
      <GuideSection title="Career Mode" icon={Trophy} iconColor="text-amber-500">
        <SubSection title="Career Timeline">
          <div className="bg-pocket-bg rounded p-2 text-xs">
            <p className="font-bold mb-1">Key Turns:</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
              <p><strong>Inspiration:</strong> 11, 23, 35, 47, 59</p>
              <p><strong>Gym 1:</strong> Turn 12</p>
              <p><strong>Gym 2:</strong> Turn 24</p>
              <p><strong>Gym 3:</strong> Turn 36</p>
              <p><strong>Gym 4:</strong> Turn 48</p>
              <p><strong>Elite Four:</strong> 60, 61, 62, 63</p>
              <p><strong>Champion:</strong> Turn 64</p>
            </div>
          </div>
        </SubSection>

        <SubSection title="Energy System">
          <div className="space-y-1 text-xs">
            <p>You start with <strong>100 energy</strong> (max 100). Training costs energy:</p>
            <div className="bg-pocket-bg rounded p-2 space-y-1">
              <p><strong>HP:</strong> 20 energy | <strong>Attack:</strong> 25 energy</p>
              <p><strong>Defense:</strong> 20 energy | <strong>Instinct:</strong> 20 energy</p>
              <p><strong className="text-green-500">Speed:</strong> -5 energy (restores energy!)</p>
            </div>
          </div>
        </SubSection>

        <SubSection title="Rest & Energy Recovery">
          <p className="text-xs"><strong>Rest</strong> randomly restores +30, +50, or +70 energy. Use it strategically to avoid low energy.</p>
        </SubSection>

        <SubSection title="Training Failure Rates">
          <div className="bg-pocket-bg rounded p-2 text-xs">
            <p className="mb-1">Failure chance increases exponentially as energy drops:</p>
            <div className="space-y-0.5">
              <p>50 energy: ~1% fail | 35 energy: ~20% fail</p>
              <p>20 energy: ~50% fail | 10 energy: ~75% fail</p>
              <p>0 energy: 99% fail</p>
            </div>
            <p className="text-pocket-text-light mt-1">Speed training uses 30 energy threshold (not 50)</p>
          </div>
        </SubSection>

        <SubSection title="Wild Battles">
          <p className="text-xs">Fight wild Pokemon for stat gains without using energy:</p>
          <div className="bg-pocket-bg rounded p-2 text-xs">
            <p><strong>Victory:</strong> +15 to a random stat, +20 Skill Points</p>
            <p className="text-pocket-text-light">Opponents scale with career progress</p>
          </div>
        </SubSection>

        <SubSection title="Learning Moves">
          <div className="space-y-1 text-xs">
            <p><strong>Base Cost:</strong> Move Stamina Cost × 3.0 Skill Points</p>
            <p><strong>Move Hints:</strong> Each hint level gives 15% discount (max 4 levels = 60% off)</p>
            <p><strong>Default Abilities:</strong> Free to learn</p>
            <p className="text-pocket-text-light">Hints appear randomly during training (15% chance when support appears)</p>
          </div>
        </SubSection>
      </GuideSection>

      {/* Type System */}
      <GuideSection title="Type System" icon={Flame} iconColor="text-orange-500">
        <SubSection title="Type Matchups">
          <p className="text-xs mb-1">Super effective attacks deal <strong>+25% damage</strong>:</p>
          <div className="bg-pocket-bg rounded p-2 text-xs">
            <div className="grid grid-cols-3 gap-x-3 gap-y-0.5">
              <p><strong className="text-orange-500">Fire</strong> → Grass</p>
              <p><strong className="text-blue-500">Water</strong> → Fire</p>
              <p><strong className="text-green-500">Grass</strong> → Water</p>
              <p><strong className="text-yellow-600">Electric</strong> → Water</p>
              <p><strong className="text-purple-500">Psychic</strong> → Fighting</p>
              <p><strong className="text-orange-600">Fighting</strong> → Electric</p>
              <p><strong className="text-gray-700">Dark</strong> → Psychic</p>
              <p><strong className="text-indigo-500">Dragon</strong> → Dragon</p>
              <p><strong className="text-slate-500">Steel</strong> → Fighting</p>
            </div>
          </div>
        </SubSection>

        <SubSection title="Type Aptitudes">
          <p className="text-xs mb-1">Each Pokemon has grades (F→UU) for each type. Higher grade = more damage + less stamina cost:</p>
          <div className="bg-pocket-bg rounded p-2 text-xs">
            <div className="grid grid-cols-4 gap-x-2 gap-y-1">
              <div><span className="font-bold" style={{color: '#000'}}>F</span> 55% dmg</div>
              <div><span className="font-bold" style={{color: '#9ca3af'}}>E</span> 70%</div>
              <div><span className="font-bold" style={{color: '#3b82f6'}}>D</span> 85%</div>
              <div><span className="font-bold" style={{color: '#22c55e'}}>C</span> 100%</div>
              <div><span className="font-bold" style={{color: '#ec4899'}}>B</span> 100%</div>
              <div><span className="font-bold" style={{color: '#f97316'}}>A</span> 115%</div>
              <div><span className="font-bold" style={{color: '#eab308'}}>S</span> 130%</div>
              <div><span className="font-bold" style={{color: '#14b8a6'}}>UU</span> 145%</div>
            </div>
            <p className="mt-1 text-pocket-text-light">Stamina costs: F=160%, B=100%, UU=55%</p>
          </div>
        </SubSection>

        <SubSection title="Strategy Aptitudes">
          <p className="text-xs mb-1">Pokemon also have grades for battle strategies (same F→UU scale). Higher grade = lower stamina costs for that playstyle:</p>
          <div className="bg-pocket-bg rounded p-2 space-y-1 text-xs">
            <p><strong className="text-green-500">Scaler:</strong> Buffs first, then attacks</p>
            <p><strong className="text-red-500">Nuker:</strong> Saves stamina for strongest moves</p>
            <p><strong className="text-yellow-500">Debuffer:</strong> Status effects and debuffs</p>
            <p><strong className="text-purple-500">Chipper:</strong> Efficient chip damage</p>
            <p><strong className="text-gray-500">MadLad:</strong> Random moves</p>
          </div>
        </SubSection>
      </GuideSection>

      {/* Battle System */}
      <GuideSection title="Battle System" icon={Swords} iconColor="text-red-500">
        <SubSection title="How Battles Work">
          <div className="space-y-1 text-xs">
            <p>• Tick-based automatic combat (100 max stamina)</p>
            <p>• Moves cost stamina and have cooldowns</p>
            <p>• Stamina regenerates every tick (even when attacking)</p>
            <p>• Battle ends when one Pokemon reaches 0 HP or timeout</p>
          </div>
        </SubSection>

        <SubSection title="Status Effects">
          <div className="bg-pocket-bg rounded p-2 text-xs">
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
              <div><strong className="text-red-500">Burn/Poison:</strong> DoT</div>
              <div><strong className="text-gray-500">Stun/Freeze:</strong> Skip turn</div>
              <div><strong className="text-yellow-500">Paralyze:</strong> 25% miss</div>
              <div><strong className="text-pink-500">Confuse:</strong> May hit self</div>
              <div><strong className="text-orange-500">Exhaust:</strong> Drain stamina</div>
              <div><strong className="text-cyan-500">Accuracy↓:</strong> 15% miss</div>
            </div>
          </div>
        </SubSection>

        <SubSection title="Stat Buffs">
          <div className="bg-pocket-bg rounded p-2 text-xs space-y-1">
            <p><strong>Single Stat Buffs:</strong> 1.5× (e.g., Swords Dance, Iron Defense, Calm Mind)</p>
            <p><strong>Dual Stat Buffs:</strong> 1.25× each (e.g., Bulk Up, Dragon Dance)</p>
            <p className="text-pocket-text-light">All buffs last 2× base duration</p>
          </div>
        </SubSection>

        <SubSection title="Weather & Terrain">
          <div className="space-y-1 text-xs">
            <p><strong>Weather:</strong> +25% damage to matching type moves</p>
            <div className="ml-2">
              <p>Sunny Day → Fire | Rain Dance → Water | Sandstorm → Rock</p>
            </div>
            <p className="mt-1"><strong>Terrain:</strong> +25% damage to specific types when grounded</p>
          </div>
        </SubSection>
      </GuideSection>

      {/* Support Cards */}
      <GuideSection title="Support Cards" icon={Users} iconColor="text-blue-500">
        <SubSection title="How Supports Work">
          <div className="space-y-1 text-xs">
            <p>• Each support specializes in one stat (HP/ATK/DEF/INS/SPD)</p>
            <p>• <strong>25-50% chance</strong> to appear during training</p>
            <p>• Higher chance when training their specialty stat</p>
            <p>• <strong>15% chance</strong> to give Move Hints when appearing</p>
            <p>• Gain <strong>+7 friendship</strong> per training with them</p>
          </div>
        </SubSection>

        <SubSection title="Training Bonuses">
          <p className="text-xs mb-1">Supports give extra stat gains when their type matches your Pokemon's type. Bonuses scale with friendship:</p>
          <div className="bg-pocket-bg rounded p-2 text-xs space-y-0.5">
            <p><strong className="text-gray-500">Common:</strong> +2-12 base → +6-25 max</p>
            <p><strong className="text-green-500">Uncommon:</strong> +4-10 base → +6-25 max</p>
            <p><strong className="text-blue-500">Rare:</strong> +4-10 base → +8-20 max</p>
            <p><strong className="text-amber-500">Legendary:</strong> +5-12 base → +10-25 max</p>
            <p className="text-pocket-text-light mt-1">Non-matching type: +0-10 (varies by support)</p>
          </div>
        </SubSection>

        <SubSection title="Friendship & Events">
          <div className="space-y-1 text-xs">
            <p>• <strong className="text-green-500">100 Friendship:</strong> Unlocks hangout events + maximum bonus</p>
            <p>• Friendship resets each career</p>
            <p>• Hangout events give stat boosts or special bonuses</p>
          </div>
        </SubSection>

        <SubSection title="Support Gacha Rates">
          <div className="bg-pocket-bg rounded p-2 text-xs">
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
              <p><span className="font-bold" style={{color: '#6b7280'}}>Common:</span> 50%</p>
              <p><span className="font-bold" style={{color: '#22c55e'}}>Uncommon:</span> 35%</p>
              <p><span className="font-bold" style={{color: '#3b82f6'}}>Rare:</span> 13%</p>
              <p><span className="font-bold" style={{color: '#f59e0b'}}>Legendary:</span> 2%</p>
            </div>
          </div>
        </SubSection>
      </GuideSection>

      {/* Inspiration System */}
      <GuideSection title="Hall of Fame & Inspirations" icon={Sparkles} iconColor="text-purple-500">
        <SubSection title="How It Works">
          <div className="space-y-1 text-xs">
            <p>• Complete careers to add Pokemon to Hall of Fame</p>
            <p>• Hall of Fame Pokemon inspire at turns <strong>11, 23, 35, 47, 59</strong></p>
            <p>• Select up to 3 Pokemon per inspiration event</p>
            <p>• Each inspiration gives stat boosts + chance to upgrade aptitudes</p>
          </div>
        </SubSection>

        <SubSection title="Inspiration Generation">
          <p className="text-xs mb-1">Server randomly picks B+ aptitudes from Hall of Fame Pokemon:</p>
          <div className="bg-pocket-bg rounded p-2 text-xs space-y-1">
            <p>• <strong>Stat:</strong> Random stat (HP/ATK/DEF/INS/SPD)</p>
            <p>• <strong>Type:</strong> Random B+ type aptitude</p>
            <p>• <strong>Strategy:</strong> Random B+ strategy aptitude</p>
            <p className="mt-1"><strong>Star Rarity:</strong> 20% 1★, 70% 2★, 10% 3★</p>
          </div>
        </SubSection>

        <SubSection title="Inspiration Effects">
          <div className="bg-pocket-bg rounded p-2 text-xs">
            <p className="mb-1"><strong>Stat Boost:</strong></p>
            <p>1★ = +10 | 2★ = +25 | 3★ = +50</p>
            <p className="mt-1 mb-1"><strong>Aptitude Upgrade Chance:</strong></p>
            <p>1★ = 3% | 2★ = 10% | 3★ = 20%</p>
            <p className="text-pocket-text-light mt-1">Upgrades raise type/strategy grade by one level</p>
          </div>
        </SubSection>
      </GuideSection>

      {/* Evolution */}
      <GuideSection title="Evolution System" icon={Zap} iconColor="text-yellow-500">
        <SubSection title="How Evolution Works">
          <div className="space-y-1 text-xs">
            <p>Pokemon evolve automatically when reaching grade thresholds:</p>
            <div className="bg-pocket-bg rounded p-2 space-y-1">
              <p><strong className="text-green-500">C Grade:</strong> Stage 1 evolution (+5% stats)</p>
              <p><strong className="text-orange-500">A Grade:</strong> Stage 2 evolution (+5% stats)</p>
            </div>
          </div>
        </SubSection>

        <SubSection title="Evolution Balance">
          <p className="text-xs mb-1">Pokemon start with different base stats based on evolution potential:</p>
          <div className="bg-pocket-bg rounded p-2 text-xs">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><strong>No Evolution</strong><br/>1.30× base</div>
              <div><strong>1-Stage</strong><br/>1.15× base</div>
              <div><strong>2-Stage</strong><br/>1.00× base</div>
            </div>
            <p className="mt-1 text-pocket-text-light text-center">All end at equal power after full evolution</p>
          </div>
        </SubSection>
      </GuideSection>

      {/* Gacha */}
      <GuideSection title="Gacha & Primos" icon={Gift} iconColor="text-pink-500">
        <SubSection title="Gacha Costs">
          <div className="space-y-1 text-xs">
            <p>• <strong className="text-amber-500">100 Primos</strong> per single roll</p>
            <p>• <strong className="text-amber-500">1000 Primos</strong> for 10-roll (no discount)</p>
            <p>• Pokemon Gacha: 9 generations, unique stats/aptitudes</p>
            <p>• Support Gacha: Training specialists</p>
          </div>
        </SubSection>

        <SubSection title="Pokemon Gacha Rates">
          <div className="bg-pocket-bg rounded p-2 text-xs">
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
              <p><span className="font-bold" style={{color: '#6b7280'}}>Common:</span> ~33%</p>
              <p><span className="font-bold" style={{color: '#22c55e'}}>Uncommon:</span> ~33%</p>
              <p><span className="font-bold" style={{color: '#3b82f6'}}>Rare:</span> ~33%</p>
              <p><span className="font-bold" style={{color: '#f59e0b'}}>Legendary:</span> 1-2%</p>
            </div>
            <p className="mt-1 text-pocket-text-light">Rates vary slightly by generation</p>
          </div>
        </SubSection>

        <SubSection title="Limit Break (Duplicates)">
          <div className="bg-pocket-bg rounded p-2 space-y-1 text-xs">
            <p>• <strong>Pokemon:</strong> Each duplicate (LB1-4) = +5% stats (max +20%)</p>
            <p>• <strong>Supports:</strong> Each duplicate unlocks progressive bonuses</p>
            <p>• Max LB → Limit Break Shards (future exchange system)</p>
          </div>
        </SubSection>

        <SubSection title="Earning Primos">
          <div className="bg-pocket-bg rounded p-2 text-xs">
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
              <p><strong>New Account:</strong> 10,000</p>
              <p><strong>Each Gym:</strong> 100</p>
              <p><strong>Each Elite Four:</strong> 100</p>
              <p><strong>Champion Bonus:</strong> +200</p>
              <p><strong>PvP Match:</strong> 100</p>
              <p><strong>Tournament 1st:</strong> 10,000</p>
              <p><strong>Tournament 2nd:</strong> 5,000</p>
              <p><strong>Tournament 3rd:</strong> 1,000</p>
            </div>
          </div>
        </SubSection>
      </GuideSection>

      {/* Competitive */}
      <GuideSection title="Competitive Modes" icon={Medal} iconColor="text-indigo-500">
        <SubSection title="Tournaments">
          <div className="space-y-1 text-xs">
            <p>• 16-player bracket every 12 hours</p>
            <p>• Must use Hall of Fame Pokemon (no duplicates)</p>
            <p>• Gym-themed: matching types get stat bonuses</p>
            <p>• <strong className="text-amber-500">Prizes:</strong> 1st=10K, 2nd=5K, 3rd=1K, 4th=100 Primos</p>
            <p>• Winners unlock special profile icons</p>
          </div>
        </SubSection>

        <SubSection title="PvP Matchmaking">
          <div className="space-y-1 text-xs">
            <p>• <strong>Best-of-3:</strong> Select 3 Hall of Fame Pokemon</p>
            <p>• Rating-based matchmaking (expands after 10-30 seconds)</p>
            <p>• AI opponent if no match found</p>
            <p>• <strong className="text-amber-500">100 Primos</strong> reward (win or lose)</p>
          </div>
        </SubSection>

        <SubSection title="Rating Divisions">
          <div className="bg-pocket-bg rounded p-2 text-xs">
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
              <p><strong className="text-amber-900">Bronze:</strong> &lt;1000</p>
              <p><strong className="text-slate-500">Silver:</strong> 1000-1199</p>
              <p><strong className="text-amber-500">Gold:</strong> 1200-1399</p>
              <p><strong className="text-slate-400">Platinum:</strong> 1400-1599</p>
              <p><strong className="text-cyan-500">Diamond:</strong> 1600-1799</p>
              <p><strong className="text-purple-600">Master:</strong> 1800+</p>
            </div>
            <p className="mt-1 text-pocket-text-light">Rating inflation below 1400 helps new players climb</p>
          </div>
        </SubSection>
      </GuideSection>

      {/* Strategy Tips */}
      <GuideSection title="Strategy Tips" icon={Brain} iconColor="text-teal-500">
        <SubSection title="Training Tips">
          <div className="space-y-1 text-xs">
            <p>• Focus on 2-3 stats (e.g., Attack+HP or Defense+HP)</p>
            <p>• Rest around 20-30 energy to avoid high failure rates</p>
            <p>• Get key supports to 100 friendship early for max bonuses</p>
            <p>• Use stat preferences to boost your focused stats</p>
            <p>• Speed training restores energy - use it strategically</p>
          </div>
        </SubSection>

        <SubSection title="Move Selection">
          <div className="space-y-1 text-xs">
            <p>• Learn moves matching your best type aptitudes (B+)</p>
            <p>• Wait for move hints to save 60% skill points</p>
            <p>• Ensure type coverage for Gyms and Elite Four</p>
            <p>• Match moves to strategy aptitudes for lower stamina costs</p>
          </div>
        </SubSection>

        <SubSection title="Hall of Fame Tips">
          <div className="space-y-1 text-xs">
            <p>• Train diverse types for better inspiration coverage</p>
            <p>• Stronger Pokemon = better inspiration star ratings</p>
            <p>• Champion runs give +200 Primos bonus</p>
            <p>• No duplicate species in PvP/Tournaments - need variety</p>
          </div>
        </SubSection>

        <SubSection title="Competitive Tips">
          <div className="space-y-1 text-xs">
            <p>• Type coverage is crucial - have answers to all types</p>
            <p>• High-grade strategies save stamina in long battles</p>
            <p>• Speed significantly affects stamina regen and cooldowns</p>
            <p>• Study tournament themes for type bonus advantages</p>
          </div>
        </SubSection>
      </GuideSection>
    </div>
  );

  if (isModal) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-white rounded-2xl w-full max-w-2xl shadow-card-lg my-4"
          onClick={(e) => e.stopPropagation()}
        >
          {showHeader && (
            <div className="sticky top-0 bg-white border-b border-pocket-bg p-4 flex justify-between items-center rounded-t-2xl z-10">
              <div className="flex items-center gap-2">
                <HelpCircle size={20} className="text-pocket-blue" />
                <h2 className="text-lg font-bold text-pocket-text">Game Guide</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-pocket-text-light hover:text-pocket-text hover:bg-pocket-bg rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          )}
          <div className="p-4 max-h-[80vh] overflow-y-auto">
            {content}
          </div>
        </motion.div>
      </div>
    );
  }

  // Embedded version (for home screen)
  return (
    <div className="bg-white rounded-2xl shadow-card overflow-hidden">
      {showHeader && (
        <div className="border-b border-pocket-bg p-4 flex items-center gap-2">
          <HelpCircle size={20} className="text-pocket-blue" />
          <h2 className="text-lg font-bold text-pocket-text">Game Guide</h2>
        </div>
      )}
      <div className="p-4">
        {content}
      </div>
    </div>
  );
};

export default GameGuide;
